import { alpha, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { styled as styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { Fragment, useMemo } from 'react';
import {
  Menu,
  menuClasses,
  MenuItem,
  MenuItemStyles,
  Sidebar,
  sidebarClasses,
} from 'react-pro-sidebar';
import { Link, useLocation } from 'react-router';
import useHasPermissions from 'src/hooks/useHasPermission';
import {
  getVisibleSecurityNavItems,
  getSecurityGroupFromPathname
} from 'src/mindtime/config/securityNav';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import { renderSecurityNavIcon, SECURITY_NAV_DIVIDER_AFTER } from './securityNavIcons';

const SIDEBAR_WIDTH = 200;
const CHILD_NAV_CLASS = 'securityChildNavItem';

const GROUP_HEADER_LABELS: Record<string, string> = {
  soc: 'SOC',
  'endpoint-security': 'EDR',
  'security-operations': 'XDR Compliance',
  'cloud-security': 'Cloud Security',
};

const isNavPathActive = (pathname: string, href: string) =>
  pathname === href || pathname.startsWith(`${href}/`);

const withNavTooltip = (label: string, node: JSX.Element) => (
  <Tooltip title={label} placement="right" arrow>
    <span style={{ display: 'block', width: '100%' }}>{node}</span>
  </Tooltip>
);

const StyledSidebar = styled(Sidebar)(({ theme }) => {
  const primary = theme.palette.primary.main;
  const navText = theme.palette.text.primary;
  const navTextMuted = theme.palette.text.secondary;

  return {
    position: 'relative',
    flex: '1 1 auto',
    alignSelf: 'stretch',
    width: '100%',
    minHeight: 0,
    height: '100%',
    maxHeight: '100%',
    [`&.${sidebarClasses.root}, & .${sidebarClasses.root}`]: {
      height: '100%',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    [`& .${sidebarClasses.container}`]: {
      flex: '1 1 auto',
      minHeight: 0,
      overflow: 'hidden',
      borderRight: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: theme.palette.background.default,
    },
    [`& .${menuClasses.root}`]: {
      flex: '1 1 auto',
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: '12px 0 8px',
    },
    [`& .${menuClasses.menuItemRoot}.${CHILD_NAV_CLASS}`]: {
      marginBottom: '2px',
    },
    [`& .${menuClasses.menuItemRoot}.${CHILD_NAV_CLASS} .${menuClasses.button}`]: {
      position: 'relative',
      height: '34px !important',
      minHeight: '34px !important',
      padding: '0 10px 0 8px !important',
      margin: '0 8px !important',
      borderRadius: '6px',
      fontFamily: 'Inter, sans-serif',
      fontSize: '13px',
      fontWeight: 500,
      color: navText,
      lineHeight: 1.3,
    },
    [`& .${menuClasses.menuItemRoot}.${CHILD_NAV_CLASS} .${menuClasses.icon}`]: {
      minWidth: '28px !important',
      width: '28px',
      marginRight: '6px !important',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    [`& .${menuClasses.menuItemRoot}.${CHILD_NAV_CLASS} .${menuClasses.label}`]: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontSize: '13px',
      fontWeight: 500,
    },
    [`& .${menuClasses.menuItemRoot}.${CHILD_NAV_CLASS}:hover .${menuClasses.button}`]: {
      backgroundColor: `${alpha(primary, 0.08)} !important`,
    },
    [`& .${menuClasses.menuItemRoot}.${CHILD_NAV_CLASS} .${menuClasses.button}.${menuClasses.active}`]: {
      color: `${primary} !important`,
      backgroundColor: `${alpha(primary, 0.12)} !important`,
      fontWeight: 600,
    },
    [`& .${menuClasses.menuItemRoot}.${CHILD_NAV_CLASS} .${menuClasses.button}.${menuClasses.active}::before`]:
    {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '5px',
      bottom: '5px',
      width: '3px',
      borderRadius: '0 2px 2px 0',
      backgroundColor: primary,
    },
    [`& .${menuClasses.menuItemRoot}.${CHILD_NAV_CLASS} .${menuClasses.button}.${menuClasses.active} .${menuClasses.label}`]:
    {
      fontWeight: 600,
      color: primary,
    },
    '& .security-sidebar-footer': {
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.palette.background.default,
      borderTop: `1px solid ${theme.palette.divider}`,
      padding: '12px 12px 14px',
      gap: '10px',
    },
    '& .security-sidebar-quick-link': {
      fontFamily: 'Inter, sans-serif',
      fontSize: '12px',
      color: navText,
      padding: '4px 2px',
      borderRadius: '4px',
      cursor: 'pointer',
    },
    '& .security-sidebar-quick-link:hover': {
      backgroundColor: alpha(primary, 0.06),
    },
    '& .security-sidebar-quick-links-label': {
      fontFamily: 'Inter, sans-serif',
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '0.12em',
      color: navTextMuted,
      textTransform: 'uppercase',
    },
    '& .security-sidebar-header': {
      fontFamily: 'Inter, sans-serif',
      fontSize: '13px',
      fontWeight: 700,
      lineHeight: 1.35,
      color: primary,
      letterSpacing: '0.01em',
    },
  };
});

const SecuritySidebar: FC = () => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const checkPermissions = useHasPermissions();
  const { isReseller } = useWazuhScope();

  const activeGroup = getSecurityGroupFromPathname(pathname);
  const headerLabel = GROUP_HEADER_LABELS[activeGroup.id] ?? activeGroup.label;

  const iconColors = useMemo(
    () => ({
      active: theme.palette.primary.main,
      inactive: theme.palette.text.secondary,
    }),
    [theme.palette.primary.main, theme.palette.text.secondary]
  );

  const visibleChildren = useMemo(
    () => getVisibleSecurityNavItems(activeGroup.children, checkPermissions, isReseller),
    [activeGroup.children, checkPermissions, isReseller]
  );

  const menuItemStyles: MenuItemStyles = useMemo(
    () => ({
      root: {
        [`&.${menuClasses.menuItemRoot}`]: {
          color: theme.palette.text.secondary,
        },
      },
      button: {},
      icon: {},
      label: {},
    }),
    [theme.palette.text.secondary]
  );

  return (
    <StyledSidebar
      collapsed={false}
      width={`${SIDEBAR_WIDTH}px`}
      backgroundColor={theme.palette.background.default}
      rootStyles={{
        border: 'none',
        flex: '1 1 auto',
        minHeight: 0,
        height: '100%',
        maxHeight: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Menu menuItemStyles={menuItemStyles}>
        <Box px={1.5} pb={1.25} pt={0.25}>
          <Typography className="security-sidebar-header">{headerLabel}</Typography>
        </Box>

        {visibleChildren.map((child, index) => {
          const childActive = isNavPathActive(pathname, child.path);
          const showDivider =
            SECURITY_NAV_DIVIDER_AFTER.has(child.id) && index < visibleChildren.length - 1;

          return (
            <Fragment key={child.id}>
              {withNavTooltip(
                child.label,
                <MenuItem
                  className={CHILD_NAV_CLASS}
                  component={<Link to={child.path} />}
                  active={childActive}
                  icon={renderSecurityNavIcon(child.id, childActive, iconColors)}
                >
                  {child.label}
                </MenuItem>
              )}
              {showDivider ? (
                <Box px={1.5} py={0.75}>
                  <Divider />
                </Box>
              ) : null}
            </Fragment>
          );
        })}
      </Menu>

      {/* <Box className="security-sidebar-footer">
        <Typography className="security-sidebar-quick-links-label">Quick links</Typography>
        <Box display="flex" flexDirection="column" style={{ gap: 2 }}>
          {SECURITY_QUICK_LINK_LABELS.map((label) => (
            <Typography key={label} role="presentation" className="security-sidebar-quick-link">
              {label}
            </Typography>
          ))}
        </Box>
        <Button
          fullWidth
          size="small"
          variant="contained"
          color="primary"
          style={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '6px',
            fontSize: '13px',
            boxShadow: 'none',
            height: 34,
          }}
          onClick={(e) => {
            e.preventDefault();
          }}
        >
          + Add new
        </Button>
      </Box> */}
    </StyledSidebar>
  );
};

export default SecuritySidebar;
